import { Box, Grid, Stack } from '@mui/material';
import { styled } from '@mui/styles';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  IConsultantImportStats,
  IConsultant,
  IConsultantSkill,
  uploadBulkConsultants,
} from 'wisestep-api';
import {
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDataTable,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { BulkImportContext } from '../../../context/BulkImportContext';

import { LayoutTheme } from './../../../listing/components/Consultants/ConsultantsListingPage';

const StyledStack = styled(Stack)(() => ({
  width: 400,
}));

interface ISpreadSheetProps {
  parsedData: {
    consultants: [] | IConsultant[];
    consultantImportStats: Partial<IConsultantImportStats>;
  };
  setNewConsultantPayload: Dispatch<
    SetStateAction<{
      consultants: [] | IConsultant[];
      consultantImportStats: Partial<IConsultantImportStats>;
    }>
  >;
}

const MapSpreadsheetColumns = ({ parsedData, setNewConsultantPayload }: ISpreadSheetProps) => {
  const { setBulkImport } = useContext(BulkImportContext);
  const [ignoreFirstRow, setIgnoreFirstRow] = useState(false);

  const methods = useForm({
    defaultValues: {
      internalVisibility: 'Public',
      visibility: 'MyWorkspace',
    },
  });
  const navigate = useNavigate();
  const options: WsSelectObj[] = [
    {
      label: 'Name*',
      value: 'name',
    },
    {
      label: 'Email',
      value: 'email',
    },
    {
      label: 'Phone',
      value: 'phoneNumber',
    },
    {
      label: 'Skills*',
      value: 'skills',
    },
    {
      label: 'Availability',
      value: 'availability',
    },
    {
      label: 'Job Title*',
      value: 'jobTitle',
    },
    {
      label: 'Experience',
      value: 'totalYearsExperience',
    },
    {
      label: 'Visa*',
      value: 'visaStatus',
    },
    {
      label: 'Relocation',
      value: 'willingToRelocate',
    },
    {
      label: 'Current Location*',
      value: 'locationSearchQuery',
    },
    {
      label: 'Preferred Location',
      value: 'chosenPlace',
    },
  ];
  const internalVisibilityOptions: WsSelectObj[] = [
    {
      label: 'Public (To All Workspace Users)',
      value: 'Public',
    },
    {
      label: 'Private (Only You)',
      value: 'Private',
    },
    {
      label: 'Custom (Selected Teams/Users)',
      value: 'Shared',
    },
  ];

  const columns = Object.keys(parsedData.consultants[0])
    .filter(
      (value) =>
        [
          'column1',
          'column2',
          'column3',
          'column4',
          'column5',
          'column6',
          'column7',
          'column8',
          'column9',
          'column10',
          'column11',
        ].indexOf(value) !== -1
    )
    .map((key) => ({
      Header: <WsSelect name={key} label={''} selectArray={options} />,
      accessor: key,
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.BODY1}>
          {cell.value}
        </WsTypography>
      ),
    }));

  const onSubmit = async (data) => {
    parsedData.consultants.forEach((consultant) => {
      for (const key in data) {
        if (data[key] == 'skills') {
          const skills = consultant[key].split(',');
          const skillObject: IConsultantSkill[] = skills.map((skill) => ({
            skill,
            experienceInYears: 0,
          }));
          consultant.skills = skillObject;
        }

        if (key == 'visibility') {
          consultant['visibility'] = data['visibility'];
          consultant['internalVisibility'] = data['internalVisibility'];
        }
        if (key !== 'internalVisibility' && key !== 'visibility' && data[key] !== 'skills') {
          consultant[data[key]] = consultant[key];
        }
        if (data[key] == 'willingToRelocate') {
          consultant.willingToRelocate = consultant[key] === 'Yes' ? true : false;
        }
      }

      [
        'column1',
        'column2',
        'column3',
        'column4',
        'column5',
        'column6',
        'column7',
        'column8',
        'column9',
        'column10',
        'column11',
      ].forEach((e) => delete consultant[e]);
      consultant.workspaceId = '';

      setNewConsultantPayload({ ...parsedData });
      if (ignoreFirstRow) {
        setNewConsultantPayload((prev) => {
          prev.consultants.splice(0, 1);
          return { ...prev };
        });
      }
    });

    try {
      const response = await uploadBulkConsultants({ ...parsedData });
      if (setBulkImport) {
        setBulkImport({
          consultants: response.data,
          consultantImportStats: parsedData.consultantImportStats,
        });
        navigate('/consultant/bulk-upload-excel/confirm');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const onChangeVisibility = (index: number, value: string) =>
    methods.setValue('visibility', value.toLocaleLowerCase() == 'no' ? 'MyWorkspace' : 'Public');

  const onChangeRow = (index: number, value: string) =>
    value.toLocaleLowerCase() == 'yes' ? setIgnoreFirstRow(true) : setIgnoreFirstRow(false);

  return (
    <FormProvider {...methods}>
      <LayoutTheme sx={{ p: 2 }}>
        <Box component="form">
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            my={5}
            sx={{ backgroundColor: WsColors.White }}
            p={2}
          >
            <StyledStack spacing={1}>
              <WsSelect
                name="internalVisibility"
                label="Share Access With Your Team*"
                selectArray={internalVisibilityOptions}
                required
              />
            </StyledStack>

            <StyledStack spacing={1}>
              <WsButtonGroup
                inputTitle="Allow Recruiters to contact me for My Consultants *"
                buttonsList={['Yes', 'No']}
                selectedIndexValue={onChangeVisibility}
              />
            </StyledStack>

            <StyledStack sx={{ width: 200 }} spacing={1}>
              <WsButtonGroup
                inputTitle={'Ignore First Row Data'}
                buttonsList={['Yes', 'No']}
                selectedIndexValue={onChangeRow}
              />
            </StyledStack>
          </Grid>

          <WsDataTable
            columns={columns}
            data={parsedData.consultants as []}
            boldItems={['column1']}
            headless={false}
            preSelectedRows={[]}
          />

          <Stack spacing={1} width="100%" direction="row" justifyContent="end" mt={2}>
            <WsButton variant="contained" onClick={() => navigate('/consultant/')} size="large">
              Cancel
            </WsButton>

            <WsButton
              variant="contained"
              onClick={() => navigate('/consultant/uploads')}
              size="large"
            >
              Back
            </WsButton>

            <WsButton variant="contained" onClick={methods.handleSubmit(onSubmit)} size="large">
              Next
            </WsButton>
          </Stack>
        </Box>
      </LayoutTheme>
    </FormProvider>
  );
};

export default MapSpreadsheetColumns;
